/**
 * Cheap "this is the same file" check used by the resume flow.
 *
 * We don't hash the full file because (a) it can be 500 MB and (b) the user
 * is impatient and would close the tab in the time a full hash takes. The
 * head + tail + size cocktail catches every realistic mismatch:
 *
 *   - Different file entirely → very different head bytes
 *   - File got re-encoded → tail bytes differ (codec stream + index moved)
 *   - File truncated → size differs
 *   - File replaced with another of the same size → astronomically unlikely
 *     to collide on head AND tail
 *
 * 64 KiB at each end is enough to cover any container's header and trailer
 * (MP4 'moov' atoms are usually < 32 KiB, WebM cues likewise) without
 * dragging in much I/O.
 */

const SAMPLE_BYTES = 64 * 1024

export async function computeIdentityHash(file: File): Promise<string> {
  const size = file.size
  const headEnd = Math.min(SAMPLE_BYTES, size)
  const tailStart = Math.max(headEnd, size - SAMPLE_BYTES)

  const head = await file.slice(0, headEnd).arrayBuffer()
  const tail = tailStart < size ? await file.slice(tailStart, size).arrayBuffer()
                                : new ArrayBuffer(0)

  // Concatenate head, tail, and size-as-8-byte-LE into a single buffer.
  const sizeBuf = new ArrayBuffer(8)
  new DataView(sizeBuf).setBigUint64(0, BigInt(size), true)

  const combined = new Uint8Array(head.byteLength + tail.byteLength + 8)
  combined.set(new Uint8Array(head), 0)
  combined.set(new Uint8Array(tail), head.byteLength)
  combined.set(new Uint8Array(sizeBuf), head.byteLength + tail.byteLength)

  const digest = await crypto.subtle.digest("SHA-256", combined)
  return hex(new Uint8Array(digest))
}

function hex(bytes: Uint8Array): string {
  let s = ""
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]
    s += (b < 16 ? "0" : "") + b.toString(16)
  }
  return s
}
